import { useTranslation } from 'react-i18next';
import { InterventionIconComponent } from '../InterventionIcon/InterventionIcon';
import ChevronIcon from '../../assets/icons/chevron.svg?react';
import type { TimelineItem } from './types';
import styles from './TreatmentTimeline.module.css';

interface TimelineRowProps {
  item: TimelineItem;
  isSelected: boolean;
  isExpanded: boolean;
  strokeColor: string;
  fillColor: string;
  onSelect?: (id: string) => void;
  onToggle: (id: string) => void;
}

export const TimelineRow = ({
  item,
  isSelected,
  isExpanded,
  strokeColor,
  fillColor,
  onSelect,
  onToggle
}: TimelineRowProps) => {
  const { t } = useTranslation();

  // item.id is optional in the generated DTO and defaulted to '' upstream, so
  // both halves of interactivity are gated on it — an id-less row renders as a
  // plain div rather than as a button that would do nothing when clicked.
  const canToggle = !!item.id && item.interventions.length > 0;
  const isClickable = (!!onSelect && !!item.id) || canToggle;
  const interventionListId = canToggle ? `timeline-interventions-${item.id}` : undefined;

  const colorStyle = { backgroundColor: fillColor, border: `1.5px solid ${strokeColor}` };
  const icon = item.icon ? (
    <div className={styles.iconCircle} style={colorStyle}>
      <InterventionIconComponent icon={item.icon} />
    </div>
  ) : (
    <div className={styles.moduleBadge} style={colorStyle}>
      {item.moduleLabel}
    </div>
  );

  const content = (
    <>
      <div className={styles.iconColumn}>{icon}</div>
      <div className={styles.itemContent}>
        <span className={styles.itemName}>{item.name}</span>
        <span className={styles.itemWeek}>
          {t('week-label')} {item.week}
        </span>
      </div>
      {canToggle && (
        <ChevronIcon
          aria-hidden="true"
          className={`${styles.expandChevron} ${isExpanded ? styles.expandChevronOpen : ''}`}
        />
      )}
    </>
  );

  // Select and expand are deliberately fused into one click — there is no
  // separate chevron hit target. Each half no-ops when unavailable (no
  // onSelect, or no interventions), so re-clicking the group that is already
  // selected just collapses its list.
  const handleClick = () => {
    if (!item.id) return;
    onSelect?.(item.id);
    if (canToggle) onToggle(item.id);
  };

  const itemClass = `${styles.timelineItem} ${isSelected ? styles.timelineItemSelected : ''}`;

  return (
    <div>
      {isClickable ? (
        <button
          type="button"
          className={`${itemClass} ${styles.timelineItemButton}`}
          onClick={handleClick}
          aria-current={isSelected || undefined}
          aria-expanded={canToggle ? isExpanded : undefined}
          aria-controls={interventionListId}
        >
          {content}
        </button>
      ) : (
        <div className={itemClass}>{content}</div>
      )}
      {/* Stays mounted while collapsed (just hidden) so the button's
          aria-controls always points at an element that exists. */}
      {canToggle && (
        <ul id={interventionListId} className={styles.interventionList} hidden={!isExpanded}>
          {item.interventions.map((intervention) => (
            <li key={intervention.id} className={styles.interventionItem}>
              <span
                className={styles.interventionBullet}
                style={{ backgroundColor: intervention.color ?? strokeColor }}
                aria-hidden="true"
              />
              {intervention.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
